// ops / hooks — the impure edge every Ops host shares: one read held as a
// Remote (fetched on open and again on each `attempt`), and the frame every
// Ops page shows (who is signed in, which deployment, which build).

export { isDenied, useOpsFrame, useRemote };

import type { OpsEnvironment } from "@bilda/app/ops";
import { Remote, Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { OpsFrame } from "../../components/Ops/OpsMenu.js";
import { buildInfo, versionWithBuild } from "../buildInfo.js";
import type { ExternalError } from "../externalError.js";
import { getAuthMe } from "../httpClient.js";
import { opsApi } from "./api.js";

const isDenied = (e: ExternalError): boolean =>
    e._tag === "External.Forbidden" || e._tag === "External.Unauthorized";

// One read, again whenever `attempt` changes; a late answer to an older
// attempt is dropped.
const useRemote = <T>(
    load: () => Promise<Result.Result<T, ExternalError>>,
    attempt: number,
): Remote.Remote<T, ExternalError> => {
    const [state, setState] = useState<Remote.Remote<T, ExternalError>>(
        Remote.notAsked,
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: `load` is a module-level read; only `attempt` asks again.
    useEffect(() => {
        let current = true;
        setState((prev) => Remote.startLoad(prev));
        load().then((r) => {
            if (!current) return;
            setState(
                Result.isOk(r)
                    ? Remote.loaded(r.value)
                    : Remote.failed(r.error),
            );
        });
        return () => {
            current = false;
        };
    }, [attempt]);
    return state;
};

const emailOf = (v: unknown): string | undefined =>
    typeof v === "object" &&
    v !== null &&
    "email" in v &&
    typeof v.email === "string"
        ? v.email
        : undefined;

const useOpsFrame = (): OpsFrame => {
    const [accountLabel, setAccount] = useState<string | undefined>();
    const [environment, setEnvironment] = useState<OpsEnvironment>("unknown");
    useEffect(() => {
        getAuthMe().then((r) => {
            if (Result.isOk(r) && r.value._tag === "authed")
                setAccount(emailOf(r.value.value));
        });
        opsApi.environment().then((r) => {
            if (Result.isOk(r)) setEnvironment(r.value);
        });
    }, []);
    return {
        accountLabel,
        environment,
        version: versionWithBuild(buildInfo),
    };
};
